
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Clock, AlertCircle, Calendar } from 'lucide-react';
import { useBoardContext } from '@/contexts/BoardContext';
import { useScreenSize } from '@/hooks/useScreenSize';
import { useCardNavigation } from '@/hooks/useCardNavigation';
import { formatDistanceToNow, isToday, isTomorrow, isPast } from 'date-fns';
import { useMemo } from 'react';

export const UpcomingDeadlines = () => {
  const { allCards, boards, allColumns } = useBoardContext();
  const { isMobile } = useScreenSize();
  const { navigateToCard } = useCardNavigation();

  const upcomingCards = useMemo(() => {
    if (!allCards || !allColumns || !boards) return [];
    
    return allCards
      .filter(card => card.end_date && !card.archived)
      .map(card => {
        const endDate = new Date(card.end_date!);
        const column = allColumns.find(col => col.id === card.column_id);
        const board = boards.find(b => b.id === column?.board_id);
        
        return {
          ...card,
          endDate,
          boardTitle: board?.title || 'Unknown Board',
          isOverdue: isPast(endDate),
          isToday: isToday(endDate),
          isTomorrow: isTomorrow(endDate),
          timeFromNow: formatDistanceToNow(endDate, { addSuffix: true })
        };
      })
      .sort((a, b) => a.endDate.getTime() - b.endDate.getTime())
      .slice(0, 5);
  }, [allCards, boards, allColumns]);

  const getStatusBadge = (card: any) => {
    if (card.isOverdue) {
      return <Badge variant="destructive" className="text-xs">Overdue</Badge>;
    }
    if (card.isToday) {
      return <Badge variant="default" className="text-xs">Due Today</Badge>;
    }
    if (card.isTomorrow) {
      return <Badge variant="secondary" className="text-xs">Due Tomorrow</Badge>;
    }
    return <Badge variant="outline" className="text-xs">{card.timeFromNow}</Badge>;
  };

  const handleCardClick = (card: any) => {
    navigateToCard(card.id, card.column_id);
  };

  return (
    <Card>
      <CardHeader className={`${isMobile ? 'pb-3' : 'pb-4'}`}>
        <CardTitle className={`${isMobile ? 'text-lg' : 'text-xl'} flex items-center gap-2`}>
          <Clock className="h-5 w-5" />
          Upcoming Deadlines
        </CardTitle>
      </CardHeader>
      <CardContent>
        {upcomingCards.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No upcoming deadlines</p>
          </div>
        ) : (
          <div className="space-y-4">
            {upcomingCards.map((card) => (
              <div 
                key={card.id} 
                className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors cursor-pointer"
                onClick={() => handleCardClick(card)}
              >
                <div className={`p-2 rounded-full flex-shrink-0 ${
                  card.isOverdue 
                    ? 'bg-red-50 dark:bg-red-950' 
                    : card.isToday 
                      ? 'bg-orange-50 dark:bg-orange-950'
                      : 'bg-blue-50 dark:bg-blue-950'
                }`}>
                  {card.isOverdue ? (
                    <AlertCircle className="h-4 w-4 text-red-600" />
                  ) : (
                    <Clock className="h-4 w-4 text-blue-600" />
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h4 className={`font-medium truncate ${isMobile ? 'text-sm' : ''} hover:text-primary transition-colors`}>
                        {card.title}
                      </h4>
                      <p className={`text-muted-foreground truncate ${isMobile ? 'text-xs' : 'text-sm'}`}>
                        {card.boardTitle}
                      </p>
                    </div>
                    {getStatusBadge(card)}
                  </div>
                  
                  {card.description && !isMobile && (
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                      {card.description}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
